import { buildApiUrl } from "../../../config";
import { arrayBufferToBase64 } from "../../../utils/base64";
import type { SlideLibrarySlide } from "../../be/types";
import {
  getSelectedSlideRefViaIntents,
  insertSlidesFromBase64ViaIntents,
  type FactSetInsertedSlideRef,
  type FactSetPresentationRef,
} from "../../../services/factset/intentBridgeService";

type Logger = (message: string) => void;

export interface SlideLibraryInsertionItem {
  id: string;
  title?: string;
  base64Slides?: string;
  pptxSources?: string[];
}

export interface SlideLibraryInsertionSuccess {
  item: SlideLibraryInsertionItem;
  insertedSlides: FactSetInsertedSlideRef[];
}

export interface SlideLibraryInsertionFailure {
  item: SlideLibraryInsertionItem;
  error: string;
}

export interface SlideLibraryInsertionResult {
  insertedCount: number;
  successes: SlideLibraryInsertionSuccess[];
  failures: SlideLibraryInsertionFailure[];
}

function stripBase64DataUri(value: string): string {
  const commaIndex = value.indexOf(",");
  return commaIndex >= 0 ? value.slice(commaIndex + 1) : value;
}

function base64ToBytes(value: string): Uint8Array {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}

function looksLikePptxZip(bytes: Uint8Array): boolean {
  return bytes.length >= 4 && bytes[0] === 0x50 && bytes[1] === 0x4b;
}

function buildLibraryFileSrc(raw: string | undefined): string | undefined {
  if (!raw) return undefined;
  if (raw.startsWith("data:") || raw.startsWith("blob:")) return raw;
  if (raw.startsWith("/api/")) return buildApiUrl(raw);
  return buildApiUrl(`/api/gcpfile/${encodeURIComponent(raw)}`);
}

export function buildLibraryPptxSrcCandidates(slide?: SlideLibrarySlide | null): string[] {
  if (!slide) return [];
  const rawCandidates = [
    slide.slide_storage_path,
    slide.slideStoragePath,
    slide.slide_url,
    slide.slideUrl,
    slide.download_url,
    slide.downloadUrl,
  ];
  const seen = new Set<string>();
  const candidates: string[] = [];
  for (const raw of rawCandidates) {
    const source = buildLibraryFileSrc(raw);
    if (!source || seen.has(source)) {
      continue;
    }
    seen.add(source);
    candidates.push(source);
  }
  return candidates;
}

export function buildLibraryPptxSrc(slide?: SlideLibrarySlide | null): string | undefined {
  return buildLibraryPptxSrcCandidates(slide)[0];
}

export function normalizeLibrarySlideSources(urls: string | string[] | undefined): string[] {
  if (!urls) return [];
  const rawUrls = Array.isArray(urls) ? urls : [urls];
  const seen = new Set<string>();
  const normalized: string[] = [];
  for (const url of rawUrls) {
    if (!url || seen.has(url)) {
      continue;
    }
    seen.add(url);
    normalized.push(url);
  }
  return normalized;
}

export async function blobToPptxBase64(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  if (!looksLikePptxZip(bytes)) {
    throw new Error("Selected slide payload is not a valid PPTX file.");
  }
  return arrayBufferToBase64(buffer);
}

export async function fetchLibrarySlideBase64(
  urls: string | string[] | undefined,
  log: Logger = () => undefined
): Promise<string | null> {
  const sources = normalizeLibrarySlideSources(urls);
  for (const url of sources) {
    try {
      if (url.startsWith("data:")) {
        const mimeEnd = url.indexOf(";");
        const mimeType = mimeEnd >= 0 ? url.slice(5, mimeEnd).toLowerCase() : "";
        const base64 = stripBase64DataUri(url);
        if (mimeType.includes("image/") || !looksLikePptxZip(base64ToBytes(base64))) {
          log("⚠️ Skipping slide-library template candidate: data URI is not a PPTX.");
          continue;
        }
        return base64;
      }

      const response = await fetch(url, { credentials: "include" });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const contentType = response.headers.get("content-type") || "";
      const buffer = await response.arrayBuffer();
      const bytes = new Uint8Array(buffer);
      if (contentType.toLowerCase().includes("image/") || !looksLikePptxZip(bytes)) {
        log(
          `⚠️ Skipping slide-library template candidate: expected PPTX bytes, got ${
            contentType || "unknown content type"
          }.`
        );
        continue;
      }
      return arrayBufferToBase64(buffer);
    } catch (error) {
      log(
        `⚠️ Could not fetch slide-library PPTX template candidate: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
    }
  }
  return null;
}

function resolveLastInsertedSlideId(insertedSlides: FactSetInsertedSlideRef[]): string | undefined {
  if (insertedSlides.length === 0) return undefined;
  const ordered = [...insertedSlides].sort(
    (a, b) => (a.insertedSlideIndex ?? 0) - (b.insertedSlideIndex ?? 0)
  );
  return ordered[ordered.length - 1]?.insertedSlideId;
}

export async function insertSlideLibraryItemsViaFactSet(input: {
  items: SlideLibraryInsertionItem[];
  log?: Logger;
  presentationRef?: Partial<FactSetPresentationRef> | null;
  initialTargetSlideId?: string;
}): Promise<SlideLibraryInsertionResult> {
  const log = input.log ?? (() => undefined);
  const successes: SlideLibraryInsertionSuccess[] = [];
  const failures: SlideLibraryInsertionFailure[] = [];

  let targetSlideId = input.initialTargetSlideId;
  if (!targetSlideId) {
    try {
      targetSlideId = (await getSelectedSlideRefViaIntents(log)).id;
    } catch (error) {
      log(
        `Could not resolve selected slide before slide-library insertion; host default will be used: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
    }
  }

  for (const item of input.items) {
    try {
      const base64Slides =
        item.base64Slides?.trim() ||
        (await fetchLibrarySlideBase64(item.pptxSources, log)) ||
        "";
      if (!base64Slides) {
        throw new Error("No usable PPTX payload was available for this slide.");
      }

      const insertedSlides = await insertSlidesFromBase64ViaIntents({
        base64Slides,
        targetSlideId,
        keepSourceFormatting: true,
        log,
        presentationRef: input.presentationRef,
      });
      successes.push({ item, insertedSlides });

      const nextTargetSlideId = resolveLastInsertedSlideId(insertedSlides);
      if (nextTargetSlideId) {
        targetSlideId = nextTargetSlideId;
      }
    } catch (error) {
      failures.push({
        item,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }

  return {
    insertedCount: successes.reduce(
      (count, success) => count + Math.max(1, success.insertedSlides.length),
      0
    ),
    successes,
    failures,
  };
}
